import {
  createContext, useCallback, useContext, useEffect, useMemo, useReducer, useRef, useState,
  type CSSProperties, type ReactNode,
} from 'react'
import type { CardCompare } from '../../../common/property-card/card-compare'
import { useLocaleDetection } from '../../../hooks/locale'
import { useLocalizedTranslation } from '../../../common/i18n'
import { fetchProperties, type ExploreProperty } from '../data'
import type { ExploreWidgetConfig, VisitorFilter } from '../config'
import { pickAccentForegroundDark } from '../hooks/useExplorerCss'
import { compareLabels, type CompareLabels } from './labels'
import {
  cleanSlugs, clearCompareStore, freshenSnap, parseShareSlugs, readCompareStore, searchCtx, snapProperty,
  withShareParam, writeCompareStore, type CompareSnap, type QuoteForCompare,
} from './model'

/**
 * Compare Homes state (#1713). Mounted by ExplorerProvider, so it shares the
 * explorer's lifetime: filter changes, load-more and re-renders never touch it,
 * and the picks live in React state, never in the DOM. Cards read nothing from
 * here directly; the grid hands each one a `CardCompare` while the mode is on.
 *
 * DATA RULE: the facts in a snapshot are the explorer's already-loaded result
 * rows, handed in by the grid through `ingest()`. The one read this module
 * makes of its own (#1816) is for a shared link naming a home the page has not
 * loaded: those slugs go to the explorer's own listing endpoint with the
 * `include_property` filter (the route and row shape the grid already loads;
 * the property-card widget reads the same way, #683). Nothing else here asks
 * the API for anything.
 */

const NO_LANGUAGE = { useAutoDetectedLocale: true, locale: 'en_US' }

/**
 * The linked homes the page lacks, by slug, in the grid's own row shape. No
 * availability filter rides along on purpose: the Conversion Kit restored a
 * shared link by id, whatever the dates, and a home the guest was sent should
 * not vanish because it is booked for the dates the sender had typed. Its price
 * cell therefore falls back to the "from" rate (no dated total was asked for).
 */
async function fetchLinkedHomes(feedId: number, slugs: string[], platformUuid?: string): Promise<Map<string, ExploreProperty>> {
  const res = await fetchProperties(
    feedId,
    // `include_property` is not a VisitorFilter key; fetchProperties forwards every key verbatim.
    { include_property: slugs.join(',') } as unknown as Partial<VisitorFilter>,
    1,
    slugs.length,
    platformUuid,
  )
  const out = new Map<string, ExploreProperty>()
  for (const property of res.result ?? []) {
    // Only what was asked for: a Central that ignored the filter would answer with page one of the feed.
    if (property.slug && slugs.includes(property.slug)) out.set(property.slug, property)
  }
  return out
}

export type CompareNotice = { kind: 'max' } | { kind: 'missing'; count: number }

interface CompareState {
  /** The mount-time restore has run (picks and link read back). Nothing is written before this. */
  hydrated: boolean
  /** A restore is waiting for the first settled result rows, or for the linked homes they lack (#1816). */
  restoring: boolean
  mode: boolean
  /** Slugs in pick order. */
  selected: string[]
  snaps: Record<string, CompareSnap>
  open: boolean
  diffOnly: boolean
  notice: CompareNotice | null
}

type Action =
  | { type: 'HYDRATED' }
  | { type: 'RESTORE_BEGIN'; selected: string[]; snaps: Record<string, CompareSnap> }
  | { type: 'RESTORE_DONE'; selected: string[]; snaps: Record<string, CompareSnap>; open: boolean; notice: CompareNotice | null }
  | { type: 'MODE'; on: boolean }
  | { type: 'TOGGLE'; slug: string; snap: CompareSnap; max: number }
  | { type: 'MAX'; max: number }
  | { type: 'REMOVE'; slug: string }
  | { type: 'OPEN'; snaps: Record<string, CompareSnap> }
  | { type: 'CLOSE' }
  | { type: 'DIFF'; on: boolean }

const INITIAL: CompareState = {
  hydrated: false, restoring: false, mode: false, selected: [], snaps: {}, open: false, diffOnly: false, notice: null,
}

function reducer(state: CompareState, action: Action): CompareState {
  switch (action.type) {
    case 'HYDRATED':
      return state.hydrated ? state : { ...state, hydrated: true }
    case 'RESTORE_BEGIN':
      // The mode comes back on with the picks; the guest has seen these cards.
      return { ...state, hydrated: true, restoring: true, mode: true, selected: action.selected, snaps: action.snaps }
    case 'RESTORE_DONE':
      return {
        ...state, restoring: false, selected: action.selected, snaps: action.snaps,
        open: action.open, notice: action.notice, mode: action.selected.length > 0 || state.mode,
      }
    case 'MODE':
      if (state.mode === action.on) return state
      // Leaving the mode drops the picks: the cards look exactly as the explorer rendered them. A restore
      // still in flight is abandoned with them (its result is ignored, see `finishRestore`).
      return action.on
        ? { ...state, mode: true, notice: null }
        : { ...state, mode: false, selected: [], open: false, notice: null, restoring: false }
    case 'TOGGLE': {
      if (state.selected.includes(action.slug)) {
        return { ...state, selected: state.selected.filter((s) => s !== action.slug), notice: null }
      }
      if (state.selected.length >= action.max) return { ...state, notice: { kind: 'max' } }
      return {
        ...state, notice: null, selected: [...state.selected, action.slug], snaps: { ...state.snaps, [action.slug]: action.snap },
      }
    }
    case 'MAX': {
      // The maximum shrank below the picks already held. Keep the first-picked homes: they are the
      // ones the tray's slots already show. Snapshots of the dropped homes are left to go stale, as
      // they are after a REMOVE.
      if (state.selected.length <= action.max) return state
      const selected = state.selected.slice(0, action.max)
      return { ...state, selected, notice: null, open: state.open && selected.length >= 2 }
    }
    case 'REMOVE': {
      const selected = state.selected.filter((s) => s !== action.slug)
      return { ...state, selected, notice: null, open: state.open && selected.length >= 2 }
    }
    case 'OPEN':
      return state.selected.length < 2 ? state : { ...state, open: true, snaps: action.snaps, notice: null }
    case 'CLOSE':
      return state.open ? { ...state, open: false } : state
    case 'DIFF':
      return state.diffOnly === action.on ? state : { ...state, diffOnly: action.on }
    default:
      return state
  }
}

export interface CompareApi {
  max: number
  buttonText: string
  /** The chrome strings for the visitor's locale (#1816): operator value → shipped table → English. */
  labels: CompareLabels
  /** Inline accent variables for the compare surfaces. Empty until there is something to override. */
  accentStyle: CSSProperties
  mode: boolean
  selected: string[]
  snaps: Record<string, CompareSnap>
  open: boolean
  diffOnly: boolean
  notice: CompareNotice | null
  /** Result rows currently loaded (the grid reports them). */
  rowCount: number
  /** The grid has finished its first load. */
  settled: boolean
  /** The header toggle's presence and visibility, so a floating pill can stand in for it. */
  entry: { mounted: boolean; visible: boolean }
  setMode: (on: boolean) => void
  toggle: (property: ExploreProperty, quote?: QuoteForCompare) => void
  remove: (slug: string) => void
  /** Drop every pick and leave the mode. */
  clear: () => void
  openTable: () => void
  closeTable: () => void
  setDiffOnly: (on: boolean) => void
  /** The grid reports the rows it renders (and any full-price quotes) after every render. */
  ingest: (rows: ExploreProperty[], quotes: Record<string, QuoteForCompare>, settled: boolean) => void
  reportEntry: (mounted: boolean, visible: boolean) => void
  /** The card props for one result, or undefined while the mode is off. */
  cardProps: (property: ExploreProperty, quote?: QuoteForCompare) => CardCompare | undefined
}

const CompareContext = createContext<CompareApi | null>(null)

/** The compare API, or null when compare is off or unavailable for this explorer. */
export function useCompare(): CompareApi | null {
  return useContext(CompareContext)
}

function replaceShareParam(slugs: string[] | null) {
  try {
    const next = withShareParam(window.location.href, slugs)
    // replaceState, never pushState: the table is not a page of its own, and Back must keep leaving the search.
    if (next !== window.location.href) window.history.replaceState(window.history.state, '', next)
  } catch { /* a locked history or a cross-origin base: the link still copies */ }
}

/**
 * What the widget hands the explorer provider so compare can read its settings.
 * Passed down (not read from the widget context) so ExplorerProvider keeps
 * mounting on its own, as it does in the map and filter tests: no host, no compare.
 */
export interface CompareHost {
  options: ExploreWidgetConfig
  feedId: number
  resolvedTheme?: string
}

export function CompareProvider({
  children, visitorFilters, host,
}: {
  children: ReactNode
  visitorFilters: Partial<VisitorFilter>
  host?: CompareHost | null
}) {
  const options = host?.options
  const feedId = host?.feedId ?? 0
  const resolvedTheme = host?.resolvedTheme
  // Each setting is read at its own path (options.compare.<leaf>), not through a local alias.
  // A hero bar is the widget's entire content and a grid-less explorer has no cards to pick.
  const available = !!options && options.compare?.enabled === true && options.search?.heroBar !== true && options.showGrid !== false
  const max = options?.compare?.max ?? 3
  const buttonText = options?.compare?.buttonText ?? 'Compare'
  const accentColor = options?.compare?.accentColor

  // #1816: the same resolution the grid chrome uses (operator value → shipped locale table → en_US,
  // shipped tables on the configured-locale path only). `_t` is a fresh closure every render, so the
  // label set is memoized on what it reads.
  const language = options?.language ?? NO_LANGUAGE
  const translations = options?.translations
  const { detectedLocale } = useLocaleDetection(language.useAutoDetectedLocale, language.locale)
  const { _t } = useLocalizedTranslation('explorer', translations, detectedLocale, language)
  const labels = useMemo(
    () => compareLabels(_t),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- _t reads exactly these
    [translations, detectedLocale, language.useAutoDetectedLocale],
  )
  const labelsRef = useRef(labels)
  labelsRef.current = labels

  const [state, dispatch] = useReducer(reducer, INITIAL)
  const [rowCount, setRowCount] = useState(0)
  const [settled, setSettled] = useState(false)
  const [entry, setEntry] = useState({ mounted: false, visible: false })
  const [clientReady, setClientReady] = useState(false)

  const rowsRef = useRef(new Map<string, { property: ExploreProperty; quote: QuoteForCompare }>())
  const filtersRef = useRef(visitorFilters)
  filtersRef.current = visitorFilters
  const optionsRef = useRef(options)
  optionsRef.current = options
  const pendingRef = useRef<{ slugs: string[]; openOnLoad: boolean; snaps: Record<string, CompareSnap> } | null>(null)
  const restoredRef = useRef(false)
  // The restore read in flight (#1816): a result for an older one, or one that lands after unmount, is dropped.
  const restoreSeq = useRef(0)
  const aliveRef = useRef(true)

  useEffect(() => { setClientReady(true) }, [])
  useEffect(() => {
    aliveRef.current = true
    return () => { aliveRef.current = false }
  }, [])

  // -- restore: once, on the client, after hydration ---------------------------------
  // A share link in the URL wins over the tab's stored picks: it is what the guest was handed.
  useEffect(() => {
    if (!available || restoredRef.current) return
    restoredRef.current = true
    const shared = cleanSlugs(parseShareSlugs(window.location.search), max)
    const saved = readCompareStore(feedId)
    const slugs = shared.length ? shared : cleanSlugs(saved?.selected, max)
    if (!slugs.length) {
      if (saved) clearCompareStore(feedId)
      dispatch({ type: 'HYDRATED' })
      return
    }
    const snaps: Record<string, CompareSnap> = {}
    for (const slug of slugs) if (saved?.snaps[slug]) snaps[slug] = saved.snaps[slug]
    pendingRef.current = { slugs, openOnLoad: shared.length >= 2, snaps }
    dispatch({ type: 'RESTORE_BEGIN', selected: slugs, snaps })
  }, [available, max, feedId])

  const snapFor = useCallback((property: ExploreProperty, quote: QuoteForCompare) => snapProperty(property, {
    options: optionsRef.current as ExploreWidgetConfig, visitorFilters: filtersRef.current, quote, labels: labelsRef.current,
  }), [])

  const stateRef = useRef(state)
  stateRef.current = state

  // -- the grid reports its rows -------------------------------------------------------
  const ingest = useCallback<CompareApi['ingest']>((rows, quotes, isSettled) => {
    const next = new Map<string, { property: ExploreProperty; quote: QuoteForCompare }>()
    for (const property of rows) if (property.slug) next.set(property.slug, { property, quote: quotes[property.id] ?? null })
    rowsRef.current = next
    setRowCount((n) => (n === next.size ? n : next.size))
    setSettled((s) => (s === isSettled ? s : isSettled))

    const pending = pendingRef.current
    if (!pending || !isSettled) return
    pendingRef.current = null
    const search = searchCtx(filtersRef.current)
    const known: Record<string, CompareSnap> = {}
    const absent: string[] = []
    for (const slug of pending.slugs) {
      const live = next.get(slug)
      if (live) known[slug] = snapFor(live.property, live.quote)
      else if (pending.snaps[slug]) known[slug] = freshenSnap(pending.snaps[slug], search)
      else absent.push(slug)
    }

    // Link order throughout. A home neither the rows nor the tab snapshot hold is counted as missing
    // unless the listing read below brought it back.
    const finishRestore = (fetched: Map<string, ExploreProperty>) => {
      const snaps: Record<string, CompareSnap> = {}
      const selected: string[] = []
      let missing = 0
      for (const slug of pending.slugs) {
        const fromFeed = known[slug] ? null : fetched.get(slug)
        const snap = known[slug] ?? (fromFeed ? snapFor(fromFeed, null) : null)
        if (snap) {
          snaps[slug] = snap
          selected.push(slug)
        } else {
          missing++
        }
      }
      dispatch({
        type: 'RESTORE_DONE', selected, snaps,
        open: pending.openOnLoad && selected.length >= 2,
        notice: missing > 0 ? { kind: 'missing', count: missing } : null,
      })
    }

    if (absent.length === 0) {
      finishRestore(new Map())
      return
    }
    // #1816: read the linked homes the page lacks through the explorer's own listing endpoint. The
    // tray keeps saying "restoring" (no persistence, the compare= param stays) until the read lands.
    const seq = ++restoreSeq.current
    const settle = (fetched: Map<string, ExploreProperty>) => {
      // Stale (a newer restore, the mode left meanwhile) or unmounted: nothing to apply.
      if (seq !== restoreSeq.current || !aliveRef.current || !stateRef.current.restoring) return
      finishRestore(fetched)
    }
    fetchLinkedHomes(feedId, absent, optionsRef.current?.filter?.platform).then(settle, () => settle(new Map()))
  }, [snapFor, feedId])

  // -- the maximum shrinks below the picks already held (#1717) -----------------------------
  // A config change, or a saved setting met by a tab that still holds older picks. A restore that
  // lands after the change brings its own list, so the check runs again when it finishes.
  useEffect(() => {
    if (available) dispatch({ type: 'MAX', max })
  }, [available, max, state.restoring])

  // -- persistence ------------------------------------------------------------------------
  useEffect(() => {
    if (!available || !state.hydrated || state.restoring) return
    if (!state.mode || state.selected.length === 0) clearCompareStore(feedId)
    else writeCompareStore(feedId, state.selected, state.snaps)
  }, [available, state.hydrated, state.restoring, state.mode, state.selected, state.snaps, feedId])

  // The open table rides in the page URL; closing it takes the param off again.
  useEffect(() => {
    if (!available || !state.hydrated) return
    if (state.open && state.selected.length >= 2) replaceShareParam(state.selected)
    else if (!state.restoring) replaceShareParam(null)
  }, [available, state.hydrated, state.restoring, state.open, state.selected])

  // -- actions ----------------------------------------------------------------------------
  const setMode = useCallback((on: boolean) => dispatch({ type: 'MODE', on }), [])
  const toggle = useCallback<CompareApi['toggle']>((property, quote) => {
    if (!property.slug) return
    dispatch({ type: 'TOGGLE', slug: property.slug, snap: snapFor(property, quote ?? null), max })
  }, [snapFor, max])
  const remove = useCallback((slug: string) => dispatch({ type: 'REMOVE', slug }), [])
  const clear = useCallback(() => dispatch({ type: 'MODE', on: false }), [])
  const closeTable = useCallback(() => dispatch({ type: 'CLOSE' }), [])
  const setDiffOnly = useCallback((on: boolean) => dispatch({ type: 'DIFF', on }), [])
  const reportEntry = useCallback((mounted: boolean, visible: boolean) => {
    setEntry((e) => (e.mounted === mounted && e.visible === visible ? e : { mounted, visible }))
  }, [])

  const openTable = useCallback(() => {
    const current = stateRef.current
    if (current.selected.length < 2) return
    // Refresh the snapshots: the guest may have changed dates since the pick, and the cards now show a
    // different total. A home that left the results keeps its name, but a total quoted for another search
    // is dropped (the cell falls back to the "from" rate).
    const search = searchCtx(filtersRef.current)
    const snaps = { ...current.snaps }
    for (const slug of current.selected) {
      const live = rowsRef.current.get(slug)
      if (live) snaps[slug] = snapFor(live.property, live.quote)
      else if (snaps[slug]) snaps[slug] = freshenSnap(snaps[slug], search)
    }
    dispatch({ type: 'OPEN', snaps })
  }, [snapFor])

  // -- accent -----------------------------------------------------------------------------
  const darkAccent = options?.darkModeColors
  const lightAccent = options?.lightModeColors
  const accentStyle = useMemo<CSSProperties>(() => {
    const custom = accentColor
    // Deterministic for SSR: only an operator-set accent is inline before the client takes over.
    if (custom) {
      return { ['--hrc-accent' as string]: custom, ['--hrc-ink' as string]: pickAccentForegroundDark(custom) ?? '#ffffff' }
    }
    if (!clientReady) return {}
    const palette = resolvedTheme === 'dark' ? darkAccent : lightAccent
    const ink = palette?.accent ? pickAccentForegroundDark(palette.accent) : null
    return ink ? { ['--hrc-ink' as string]: ink } : {}
  }, [accentColor, clientReady, resolvedTheme, darkAccent, lightAccent])

  const cardProps = useCallback<CompareApi['cardProps']>((property, quote) => {
    if (!state.mode || !property.slug) return undefined
    const slug = property.slug
    return {
      picked: state.selected.includes(slug),
      label: buttonText,
      pickedLabel: labels.added,
      ariaLabel: labels.pickAria(buttonText, property.title),
      style: accentStyle,
      onToggle: () => toggle(property, quote),
    }
  }, [state.mode, state.selected, buttonText, labels, accentStyle, toggle])

  const api = useMemo<CompareApi>(() => ({
    max, buttonText, labels, accentStyle,
    mode: state.mode, selected: state.selected, snaps: state.snaps, open: state.open,
    diffOnly: state.diffOnly, notice: state.notice, rowCount, settled, entry,
    setMode, toggle, remove, clear, openTable, closeTable, setDiffOnly, ingest, reportEntry, cardProps,
  }), [
    max, buttonText, labels, accentStyle, state.mode, state.selected, state.snaps, state.open, state.diffOnly,
    state.notice, rowCount, settled, entry, setMode, toggle, remove, clear, openTable, closeTable, setDiffOnly,
    ingest, reportEntry, cardProps,
  ])

  return <CompareContext.Provider value={available ? api : null}>{children}</CompareContext.Provider>
}
